package devxertech.com.pages.services.chatbotDevelopment

import androidx.compose.runtime.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import devxertech.com.component.CommonButton
import devxertech.com.theme.SitePalette
import devxertech.com.utils.Constants
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.TouchEvent

private const val DEMO_VIDEO = "https://res.cloudinary.com/dpkrdodfd/video/upload/v1791439317/Screen_Recording_2026-10-08_104751_t10coh.mp4"
private const val DEMO_THUMBNAIL = "/ai-assistant-demo-thumbnail.webp"

@Composable
fun AiAssistantDemo(breakpoint: Breakpoint, sitePalette: SitePalette, showProjectCta: Boolean = true) {
    var videoFailed by remember { mutableStateOf(false) }
    val compact = breakpoint <= Breakpoint.MD

    Section(attrs = {
        id("ai-assistant-demo")
        attr("aria-labelledby", "ai-assistant-demo-heading")
        style {
            width(100.percent)
            property("box-sizing", "border-box")
            padding(if (compact) 36.px else 60.px, if (compact) 20.px else 40.px)
            backgroundColor(sitePalette.softwareSectionBackground.rgb)
            fontFamily(Constants.FONT_FAMILY)
            color(sitePalette.commonSecondaryButtonColor.rgb)
        }
    }) {
        Div(attrs = {
            style {
                property("max-width", "1100px")
                property("margin", "0 auto")
            }
        }) {
            H2(attrs = {
                id("ai-assistant-demo-heading")
                style {
                    margin(0.px, 0.px, 28.px)
                    fontFamily(Constants.ROBOTO_FONT)
                    fontSize(if (compact) 26.px else 35.px)
                    fontWeight(700)
                    textAlign("center")
                }
            }) { Text("See Our AI Assistant in Action") }

            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("flex-direction", if (compact) "column" else "row")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("gap", if (compact) "24px" else "48px")
                }
            }) {
                Div(attrs = {
                    style {
                        width(100.percent)
                        property("max-width", "660px")
                        property("flex", "1 1 auto")
                        property("min-width", "0")
                    }
                }) {
                    Video(attrs = {
                        attr("src", DEMO_VIDEO)
                        attr("controls", "")
                        attr("playsinline", "")
                        attr("preload", "none")
                        attr("poster", DEMO_THUMBNAIL)
                        attr("tabindex", "0")
                        attr("aria-label", "HireNorth AI assistant demonstration. Click the poster or press Enter or Space to play.")
                        // Keep the fallback usable for network and decoding failures.
                        ref { element ->
                            var posterVisible = true
                            var suppressSpaceRelease = false
                            val startPlayback = {
                                element.play().catch {
                                    videoFailed = true
                                    Unit
                                }
                            }
                            val errorListener = EventListener { videoFailed = true }
                            val playListener = EventListener { posterVisible = false }
                            val clickListener = EventListener { event ->
                                val click = event as MouseEvent
                                // Leave the native control bar's play, seek, volume and menu actions to the browser.
                                if (posterVisible && click.clientY < element.getBoundingClientRect().bottom - 64) {
                                    event.preventDefault()
                                    startPlayback()
                                }
                            }
                            val keyListener = EventListener { event ->
                                val key = event as KeyboardEvent
                                if (posterVisible && key.target == element && !key.repeat &&
                                    (key.key == "Enter" || key.key == " ")) {
                                    event.preventDefault()
                                    suppressSpaceRelease = key.key == " "
                                    startPlayback()
                                }
                            }
                            val keyUpListener = EventListener { event ->
                                val key = event as KeyboardEvent
                                if (suppressSpaceRelease && key.key == " ") {
                                    suppressSpaceRelease = false
                                    // Native controls also toggle Space on release; avoid immediately pausing.
                                    event.preventDefault()
                                    event.stopPropagation()
                                }
                            }
                            val touchListener = EventListener { event ->
                                val touch = (event as TouchEvent).changedTouches.item(0)
                                if (posterVisible && touch != null &&
                                    touch.clientY < element.getBoundingClientRect().bottom - 64) {
                                    event.preventDefault()
                                    startPlayback()
                                }
                            }
                            element.addEventListener("error", errorListener)
                            element.addEventListener("play", playListener)
                            element.addEventListener("click", clickListener)
                            element.addEventListener("keydown", keyListener)
                            element.addEventListener("keyup", keyUpListener, true)
                            element.addEventListener("touchend", touchListener)
                            onDispose {
                                element.removeEventListener("error", errorListener)
                                element.removeEventListener("play", playListener)
                                element.removeEventListener("click", clickListener)
                                element.removeEventListener("keydown", keyListener)
                                element.removeEventListener("keyup", keyUpListener, true)
                                element.removeEventListener("touchend", touchListener)
                            }
                        }
                        style {
                            display(DisplayStyle.Block)
                            width(100.percent)
                            property("height", "auto")
                            property("aspect-ratio", "1914 / 944")
                            property("object-fit", "contain")
                            borderRadius(10.px)
                            backgroundColor(Color.black)
                        }
                    }) {
                        Text("Your browser cannot play this video. Use the Open video link below.")
                    }
                    if (videoFailed) {
                        P(attrs = { attr("role", "status") }) {
                            Text("The video could not be loaded. Open it directly to watch.")
                        }
                    }
                    if (videoFailed) A(href = DEMO_VIDEO, attrs = {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                        style {
                            display(DisplayStyle.InlineBlock)
                            marginTop(12.px)
                            padding(8.px, 0.px)
                            color(sitePalette.commonSecondaryButtonColor.rgb)
                        }
                    }) { Text("Open video") }
                }

                Div(attrs = {
                    style {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                        width(100.percent)
                    }
                }) {
                    P(attrs = {
                        style { fontSize(18.px); lineHeight("1.7"); margin(0.px, 0.px, 24.px) }
                    }) {
                        Text("See the HireNorth AI Receptionist answer a visitor's HRIS pricing question in a website chat widget. The conversation explains worker-based pricing and asks about the visitor's workforce business to assess fit for a demo.")
                    }
                    P(attrs = {
                        style { fontSize(18.px); lineHeight("1.7"); margin(0.px, 0.px, 24.px) }
                    }) {
                        Text("The interface offers Type and Call options, a message field, and a Send button. Watch the recording to see the assistant in action.")
                    }
                    if (showProjectCta) CommonButton(text = "Discuss a Similar Project", onClick = {}, linkPath = "/contact-us", buttonWidth = 230)
                }
            }
        }
    }
}
